import { mediaService } from '~/utils/storyblok'

export type Media = {
  alt?: string
  filename?: string | { filename: string }
  focus?: string
  title?: string
}
export default function useMedia({
  media,
  maxWidth = ref(960),
  maxHeight = ref(0),
  quality = ref(65),
}: {
  media: Ref<Media>
  fit?: Ref<string>
  maxWidth?: Ref<number>
  maxHeight?: Ref<number>
  quality?: Ref<number>
}) {
  const defaultFocus = ref(50)
  const isLoaded = ref(false)

  const altText = computed(() => {
    return media.value?.alt || ''
  })

  const mediaUrl = computed(() => {
    // Remove any existing Media Service filters from the URL
    if (typeof media.value?.filename === 'string') {
      return media.value?.filename.split('/m/')[0]
    } else if (
      typeof media.value?.filename === 'object' &&
      media.value?.filename?.filename
    ) {
      return media.value?.filename.filename.split('/m/')[0]
    } else {
      return ''
    }
  })

  const dimensions = computed(() => {
    const urlParts = (mediaUrl.value || '').split('/')
    // if the dimensions are not in the url, then we don't know these
    // so be sure to work around these possibly being zero!
    if (urlParts.length < 8 || !urlParts[5].match('x')) {
      return {
        width: 0,
        height: 0,
      }
    }
    return {
      width: Number(urlParts[5].split('x')[0]),
      height: Number(urlParts[5].split('x')[1]),
    }
  })

  const resizedImage = computed(() => {
    if (!mediaUrl.value) {
      return ''
    }
    const width = shouldResize.value ? maxWidth.value : dimensions.value?.width
    const height = shouldResize.value
      ? maxHeight.value
      : dimensions.value?.height

    return mediaService(mediaUrl.value, width, height, quality.value)
  })

  const shouldResize = computed(
    () =>
      dimensions.value?.width >= maxWidth?.value &&
      dimensions.value?.height >= maxHeight?.value,
  )

  const focus = computed(() => {
    return media.value?.focus
      ? media.value.focus.split(':')?.[0].split('x').map(Number)
      : null
  })

  const focusX = computed<number | null>(() => {
    return focus.value?.[0] || null
  })

  const focusY = computed(() => {
    return focus.value?.[1] || null
  })

  const focusXinPercent = computed(() => {
    return focusX.value && dimensions.value.width
      ? (focusX.value / dimensions.value.width) * 100
      : defaultFocus.value
  })

  const focusYinPercent = computed(() => {
    return focusX.value && dimensions.value?.height
      ? (focusY.value / dimensions.value.height) * 100
      : defaultFocus.value
  })

  const focusInPercent = computed(() => {
    return focusXinPercent.value + '% ' + focusYinPercent.value + '%'
  })

  function onLoadedImage() {
    nextTick(() => {
      isLoaded.value = true
    })
  }

  return {
    defaultFocus,
    isLoaded,
    altText,
    dimensions,
    mediaUrl,
    resizedImage,
    onLoadedImage,
    focusInPercent,
    shouldResize,
  }
}
